import { isNumber } from 'fitify-types/src/type-guards/common'
import { UnitSystem } from 'fitify-types/src/types/common'
import { User } from 'fitify-types/src/types/user'
import { getNavigatorLocale } from 'fitify-ui/src/utils/language'
import { clearItem, getItem, setItem } from 'fitify-ui/src/utils/storage'
import { IMPERIAL_UNITS_LOCALES } from 'fitify-ui-onboarding/src/types/data'
import { CM_PER_INCH, KG_PER_LBS } from 'fitify-ui-onboarding/src/utils/unit'
import { clamp } from 'fitify-utils/src/number'
import { deepMerge } from 'fitify-utils/src/properties'
import {
  HEIGHT_IMPERIAL_MAX,
  HEIGHT_IMPERIAL_MIN,
  HEIGHT_METRIC_MAX,
  HEIGHT_METRIC_MIN,
  WEIGHT_IMPERIAL_MAX,
  WEIGHT_IMPERIAL_MIN,
  WEIGHT_METRIC_MAX,
  WEIGHT_METRIC_MIN,
} from 'fitify-utils/src/validations/user'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react'

import type {
  DataPath,
  DataPathState,
  DataState,
  FunnelDataPath,
} from 'types/state'

type Actions =
  | { type: 'set-state'; payload?: DataState }
  | { type: 'set'; path: DataPath; payload: DataPathState<DataPath> }

const initialState: DataState = {
  common: {},
  chf01: {
    gender: User.Profile.Gender.Female,
    units: undefined,
  },
  chm01: {
    gender: User.Profile.Gender.Male,
    units: undefined,
  },
  f01: {
    gender: User.Profile.Gender.Female,
    units: undefined,
  },
  hopif01: {
    gender: User.Profile.Gender.Female,
    units: undefined,
  },
  m01: {
    gender: User.Profile.Gender.Male,
    units: undefined,
  },
  milicaf01: {
    gender: User.Profile.Gender.Female,
    units: undefined,
  },
  milicam01: {
    gender: User.Profile.Gender.Male,
    units: undefined,
  },
  tchm01: {
    gender: User.Profile.Gender.Male,
    units: undefined,
  },
  wallpif01: {
    gender: User.Profile.Gender.Female,
    units: undefined,
  },
  shared: {},
}

const initializeUnits = (state: DataState): DataState => {
  const locale = getNavigatorLocale()
  const hasImperialUnits = IMPERIAL_UNITS_LOCALES.includes(locale)

  return Object.entries(state).reduce<DataState>((acc, [key, subState]) => {
    if ('units' in subState && !subState.units) {
      return {
        ...acc,
        [key]: {
          ...subState,
          units: hasImperialUnits ? UnitSystem.Imperial : UnitSystem.Metric,
        },
      }
    }

    return acc
  }, state)
}

/**
 * Updates weight and height values when units are changed.
 */
const updateUnits = <TPath extends DataPath>({
  previousSubState,
  updatedSubState,
}: {
  previousSubState: DataPathState<TPath>
  updatedSubState: DataPathState<TPath>
}): DataPathState<TPath> => {
  if (
    'units' in updatedSubState &&
    'units' in previousSubState &&
    previousSubState.units !== updatedSubState.units
  ) {
    const unitKeys = ['weight', 'goal_weight', 'height'] as const

    return unitKeys.reduce<DataPathState<TPath>>((subState, key) => {
      if (
        key in previousSubState &&
        key in updatedSubState &&
        isNumber(previousSubState[key]) &&
        isNumber(updatedSubState[key]) &&
        previousSubState[key] === updatedSubState[key]
      ) {
        const units = updatedSubState.units
        let value = updatedSubState[key]
        switch (key) {
          case 'weight':
          case 'goal_weight': {
            if (units === UnitSystem.Metric) {
              value = clamp(
                value * KG_PER_LBS,
                WEIGHT_METRIC_MIN,
                WEIGHT_METRIC_MAX
              )
            } else if (units === UnitSystem.Imperial) {
              value = clamp(
                value / KG_PER_LBS,
                WEIGHT_IMPERIAL_MIN,
                WEIGHT_IMPERIAL_MAX
              )
            }
            break
          }
          case 'height':
            if (units === UnitSystem.Metric) {
              value = clamp(
                value * CM_PER_INCH,
                HEIGHT_METRIC_MIN,
                HEIGHT_METRIC_MAX
              )
            } else {
              value = clamp(
                value / CM_PER_INCH,
                HEIGHT_IMPERIAL_MIN,
                HEIGHT_IMPERIAL_MAX
              )
            }
            break
          default:
            break
        }
        return {
          ...subState,
          [key]: Math.round(value),
        }
      }
      return subState
    }, updatedSubState)
  }

  return updatedSubState
}

const reducer = (state: DataState, action: Actions): DataState => {
  switch (action.type) {
    case 'set-state': {
      const updatedState = initializeUnits(action.payload || state)
      setItem('session', '@fitify/plan', JSON.stringify(updatedState))
      return updatedState
    }
    case 'set': {
      const previousSubState = state[action.path]
      const updatedSubState = {
        ...previousSubState,
        ...action.payload,
      }

      const subState = updateUnits({
        previousSubState,
        updatedSubState,
      })

      const updatedState = {
        ...state,
        [action.path]: subState,
      }

      setItem('session', '@fitify/plan', JSON.stringify(updatedState))
      return updatedState
    }
    default:
      return state
  }
}

export const DataContext = createContext<{
  data: DataState
  set: <TPath extends DataPath>(
    path: TPath,
    payload: DataPathState<TPath>
  ) => void
  clear: () => void
}>({
  data: initialState,
  set: () => void 0,
  clear: () => void 0,
})

export function useAppState() {
  const [isLoading, setIsLoading] = useState(true)
  const [data, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    let sessionState: DataState | undefined
    try {
      const planSession = getItem('session', '@fitify/plan')
      if (planSession) {
        sessionState = JSON.parse(planSession)
      }
    } catch (err) {
      console.error(err)
    }
    dispatch({
      type: 'set-state',
      payload: deepMerge(initialState, sessionState),
    })
    setIsLoading(false)
  }, [])

  const set = useCallback(
    <TPath extends DataPath>(
      path: TPath,
      payload: Partial<DataPathState<TPath>>
    ) => {
      dispatch({ type: 'set', path, payload })
    },
    []
  )

  const clear = useCallback(() => {
    clearItem('session', '@fitify/plan')
    clearItem('session', '@fitify/subscription/activePromo')
    dispatch({ type: 'set-state', payload: initialState })
  }, [])

  return { data, set, clear, isLoading }
}

export const useDataState = () => {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error('useDataState must be used within a DataContextProvider')
  }
  return context
}

export const useFunnelState = <TFunnel extends FunnelDataPath>(
  funnelPath: TFunnel
) => {
  const context = useDataState()
  return useMemo(() => {
    return {
      data: context.data[funnelPath],
      set: (newValue: DataPathState<TFunnel>) =>
        context.set(funnelPath, newValue),
    }
  }, [context, funnelPath])
}
